import {CommonModule} from '@angular/common';
import {Component} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {Router} from '@angular/router';
import {FaIconLibrary, FontAwesomeModule} from '@fortawesome/angular-fontawesome';
import {faAngleLeft, faAngleRight, faMagnifyingGlass, faPen, faSquarePlus} from '@fortawesome/free-solid-svg-icons';
import {ToastrService} from 'ngx-toastr';
import {ApiService} from '../../services/api.service';
import {DEFAULT_TABLE_PAGE_SIZE_OPTIONS} from '../../shared/constants/pagination.constants';

type WebhookProviderRow = {
  id: number;
  name: string;
  display_name: string;
  is_active: boolean;
  created_at: string;
  _toggling?: boolean;
};

@Component({
    selector: 'app-partners',
    imports: [CommonModule, FormsModule, FontAwesomeModule],
    templateUrl: './partners.component.html',
    styleUrl: './partners.component.css'
})
export class PartnersComponent {
    readonly pageSizeOptions = DEFAULT_TABLE_PAGE_SIZE_OPTIONS;
  providers: WebhookProviderRow[] = [];
  searchText = '';
  recordsPerPage = 10;
  currentPage = 1;
  totalRecords = 0;
  nextPage: number | null = null;
  previousPage: number | null = null;
  loading = false;

  constructor(
    private apiService: ApiService,
    private router: Router,
    private toastr: ToastrService,
    library: FaIconLibrary
  ) {
    library.addIcons(
      faSquarePlus,
      faPen,
      faMagnifyingGlass,
      faAngleLeft,
      faAngleRight
    );
  }

  ngOnInit() {
    this.fetchProviders();
  }

  get totalPages(): number {
    const totalPages = Math.ceil(this.totalRecords / this.recordsPerPage);
    return totalPages === 0 ? 1 : totalPages;
  }

  fetchProviders() {
    this.loading = true;
    this.apiService
      .getWebhookProviders(this.currentPage, this.recordsPerPage, this.searchText)
      .subscribe({
        next: (response: any) => {
          this.providers = response?.results ?? [];
          this.totalRecords = response?.count ?? 0;
          this.nextPage = response?.next ?? null;
          this.previousPage = response?.previous ?? null;
          this.loading = false;
        },
        error: (err) => {
          this.providers = [];
          this.totalRecords = 0;
          this.nextPage = null;
          this.previousPage = null;
          this.loading = false;
          if (err?.error?.detail) {
            this.toastr.error(err.error.detail, 'Error');
          } else if (err?.status !== 403 && err?.status !== 0) {
            this.toastr.error('Unable to load technology partners.', 'Error');
          }
        }
      });
  }

  onSearch() {
    this.currentPage = 1;
    this.fetchProviders();
  }

  resetFilters() {
    if (!this.searchText) {
      return;
    }
    this.searchText = '';
    this.currentPage = 1;
    this.fetchProviders();
  }

  next() {
    if (!this.nextPage) {
      return;
    }
    this.currentPage += 1;
    this.fetchProviders();
  }

  previous() {
    if (!this.previousPage) {
      return;
    }
    this.currentPage -= 1;
    this.fetchProviders();
  }

  selectPageSize() {
    this.currentPage = 1;
    this.fetchProviders();
  }

  openCreate() {
    this.router.navigate(['admin/partners/new']);
  }

  openEdit(id: number) {
    this.router.navigate([`admin/partners/${id}/edit`]);
  }

  togglePartnerStatus(provider: WebhookProviderRow, event?: Event) {
    const target = event?.target as HTMLInputElement | undefined;
    const nextStatus = target ? target.checked : !provider.is_active;
    const previousStatus = provider.is_active;
    provider.is_active = nextStatus;
    provider._toggling = true;
    this.apiService
      .updateWebhookProvider(provider.id, { is_active: nextStatus })
      .subscribe({
        next: () => {
          provider._toggling = false;
          this.toastr.success(
            `Technology partner ${nextStatus ? 'enabled' : 'disabled'} successfully.`,
            'Success'
          );
        },
        error: (err) => {
          provider.is_active = previousStatus;
          provider._toggling = false;
          if (err?.error?.detail) {
            this.toastr.error(err.error.detail, 'Error');
          } else if (err?.status !== 403 && err?.status !== 0) {
            this.toastr.error('Something went wrong!', 'Error');
          }
        }
      });
  }

  onProviderResult(providerEvent: { searchText: string, results: any[], totalRecords: number, nextPage: number | null, previousPage: number | null }) {
    this.currentPage = 1;
    this.recordsPerPage = 10;
    this.previousPage = providerEvent.previousPage;
    this.nextPage = providerEvent.nextPage;
    this.searchText = providerEvent.searchText;
    this.providers = providerEvent.results;
    this.totalRecords = providerEvent.totalRecords;
  };
}
